import React, { useEffect, useRef, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import styles from "./GoogleCustomerAuthButton.module.css";

const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_SCOPES = "openid email profile";

let googleScriptPromise;

const loadGoogleIdentityScript = () => {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  if (googleScriptPromise) {
    return googleScriptPromise;
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleScriptPromise;
};

const GoogleCustomerAuthButton = ({
  disabled = false,
  label,
  onCredential,
  onError,
}) => {
  const tokenClientRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || typeof window === "undefined") return undefined;

    let isMounted = true;

    loadGoogleIdentityScript()
      .then(() => {
        if (!isMounted || !window.google?.accounts?.oauth2) {
          return;
        }

        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPES,
          callback: (response) => {
            if (response?.access_token) {
              onCredentialRef.current?.({ accessToken: response.access_token });
              return;
            }

            onErrorRef.current?.("Google авторизацію скасовано або не завершено.");
          },
          error_callback: (error) => {
            const isCanceled = error?.type === "popup_closed";
            onErrorRef.current?.(
              isCanceled
                ? "Google авторизацію скасовано."
                : "Google авторизацію скасовано або не завершено."
            );
          },
        });
        setReady(true);
      })
      .catch(() => {
        if (!isMounted) return;
        setScriptError(true);
        onErrorRef.current?.(
          "Не вдалося завантажити Google авторизацію. Перевірте з'єднання або спробуйте пізніше."
        );
      });

    return () => {
      isMounted = false;
      tokenClientRef.current = null;
    };
  }, []);

  const handleClick = () => {
    if (disabled) return;

    if (!tokenClientRef.current) {
      onErrorRef.current?.("Google авторизація ще завантажується. Спробуйте за кілька секунд.");
      return;
    }

    tokenClientRef.current.requestAccessToken({ prompt: "select_account" });
  };

  if (!GOOGLE_CLIENT_ID || scriptError) {
    return (
      <div className={styles.wrapper}>
        <button className={styles.fallbackButton} type="button" disabled>
          {label}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <button
        className={styles.googleButton}
        type="button"
        disabled={disabled || !ready}
        onClick={handleClick}
      >
        <FcGoogle className={styles.googleIcon} aria-hidden="true" />
        <span>{label}</span>
      </button>
    </div>
  );
};

export default GoogleCustomerAuthButton;
